/**
 * Utility functions để kiểm tra authentication
 */

/**
 * Kiểm tra xem user đã đăng nhập chưa (client-side)
 */
export function isAuthenticated(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();

  if (!accessToken) return !!refreshToken;
  if (!isValidTokenFormat(accessToken)) {
    clearTokens();
    return false;
  }
  const exp = getJwtExp(accessToken);
  if (!exp) {
    clearTokens();
    return false;
  }
  
  // Nếu access token hết hạn
  if (exp * 1000 <= Date.now()) {
    // Nếu có refresh token thì vẫn coi là authenticated (để axios interceptor xử lý refresh)
    if (refreshToken) {
      return true;
    }
    // Không có refresh token thì logout
    clearTokens();
    return false;
  }
  return true;
}

function decodeBase64Url(input: string): string {
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4;
  const padded = pad ? base64 + '='.repeat(4 - pad) : base64;
  return atob(padded);
}

export function getJwtExp(token: string): number | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(decodeBase64Url(parts[1]));
    return typeof payload?.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/**
 * Lấy access token từ localStorage
 */
export function getAccessToken(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return localStorage.getItem('access_token') || sessionStorage.getItem('access_token') || getCookieValue('access_token');
}

/**
 * Lấy refresh token từ localStorage
 */
export function getRefreshToken(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token') || getCookieValue('refresh_token');
}

function getCookieValue(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  if (!match) return null;
  return decodeURIComponent(match.split('=').slice(1).join('='));
}

/**
 * Lưu tokens vào localStorage/sessionStorage và cookie
 */
export function setTokens(accessToken: string, refreshToken: string, rememberMe: boolean = true): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (rememberMe) {
    // Lưu lâu dài
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');

    // Cookie 30 ngày
    const maxAge = 30 * 24 * 60 * 60; // 30 days
    document.cookie = `access_token=${accessToken}; path=/; max-age=${maxAge}; SameSite=Lax`;
    document.cookie = `refresh_token=${refreshToken}; path=/; max-age=${maxAge}; SameSite=Lax`;
  } else {
    // Chỉ lưu cho phiên làm việc hiện tại
    sessionStorage.setItem('access_token', accessToken);
    sessionStorage.setItem('refresh_token', refreshToken);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');

    // Session cookie (không có max-age)
    document.cookie = `access_token=${accessToken}; path=/; SameSite=Lax`;
    document.cookie = `refresh_token=${refreshToken}; path=/; SameSite=Lax`;
  }
}

/**
 * Xóa tokens khỏi localStorage, sessionStorage và cookie
 */
export function clearTokens(): void {
  if (typeof window === 'undefined') {
    return;
  }
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  sessionStorage.removeItem('access_token');
  sessionStorage.removeItem('refresh_token');
  
  // Xóa cookie
  document.cookie = 'access_token=; path=/; max-age=0';
  document.cookie = 'refresh_token=; path=/; max-age=0';
}

/**
 * Kiểm tra xem token có hợp lệ không (chỉ check format, không verify với server)
 */
export function isValidTokenFormat(token: string | null): boolean {
  if (!token) {
    return false;
  }
  
  // JWT token có format: header.payload.signature (3 parts separated by dots)
  const parts = token.split('.');
  return parts.length === 3;
}

export function getDashboardPathForRole(role?: string | null, isBoard?: boolean): string {
  const r = String(role || '').trim().toUpperCase();
  if (!r) return '/dashboard';
  if (r === 'ADMIN' || isBoard) return '/admin';
  return '/dashboard';
}
